import {
  Box,
  TextInput,
  Select,
  Button,
  Stack,
  Grid,
  Text,
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconCircleCheckFilled } from '@tabler/icons-react'

import {
  DARK_BROWN,
  LIGHT_BROWN,
  TEXT_ICONS_SECONDARY,
} from '~/widgets/shared/colors'
import { TariffCalculatorLoadingAnimation } from '~/widgets/TariffCalculator/components/TariffCalculatorLoadingAnimation'
import classes from '~/widgets/TariffCalculator/components/TariffCalculatorSubmissionView.module.css'
import { TARIFF_CALC_STRINGS } from '~/widgets/TariffCalculator/strings'
import HeroImage from '~/widgets/TariffCalculator/tariff-hero-image.svg'
import { COUNTRY_OPTIONS } from '~/widgets/TariffCalculator/utils'

interface FormValues {
  product: string
  country: string | null
}

interface TariffCalculatorSubmissionViewVariantProps {
  form: UseFormReturnType<FormValues>
  isCalculating: boolean
  onSubmit: (values: FormValues) => void
}

export function TariffCalculatorSubmissionView({
  form,
  isCalculating,
  onSubmit,
}: TariffCalculatorSubmissionViewVariantProps) {
  return (
    <Grid gutter="xl" align="center">
      {/* Left column - Form box */}
      <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 1, md: 1 }}>
        <Box
          p="xl"
          bg="#f8f9fa"
          style={{
            borderRadius: 12,
            boxShadow:
              '0px 0px 0.5px 0px rgba(0, 0, 0, 0.3), 0px 1px 3px 0px rgba(0, 0, 0, 0.15)',
          }}
        >
          <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack gap="lg">
              {/* Title Section */}
              <Box>
                <Text fz={32} fw={600} lh="40px" c="#212529">
                  Navigate Tariffs, Build a Plan,
                  <br />
                  <span style={{ color: '#b27444' }}>Save millions.</span>
                </Text>
              </Box>

              {/* Value Props */}
              <Stack gap="xs" visibleFrom="sm">
                <Box
                  display="flex"
                  p={4}
                  style={{
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <IconCircleCheckFilled
                    size={16}
                    style={{ color: '#b27444', flexShrink: 0 }}
                  />
                  <Text
                    fz={16}
                    fw={600}
                    lh="24px"
                    c="#212529"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    Automate HTS classification for any item
                  </Text>
                </Box>

                <Box
                  display="flex"
                  p={4}
                  style={{
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <IconCircleCheckFilled
                    size={16}
                    style={{ color: '#b27444', flexShrink: 0 }}
                  />
                  <Text
                    fz={16}
                    fw={600}
                    lh="24px"
                    c="#212529"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    Instantly calculate tariff exposure for any product or BOM
                  </Text>
                </Box>

                <Box
                  display="flex"
                  p={4}
                  style={{
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <IconCircleCheckFilled
                    size={16}
                    style={{ color: '#b27444', flexShrink: 0 }}
                  />
                  <Text
                    fz={16}
                    fw={600}
                    lh="24px"
                    c="#212529"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    Access product intelligence that uncovers savings and
                    sourcing opportunities
                  </Text>
                </Box>
              </Stack>

              {/* Form Fields */}
              <Stack gap="sm">
                <TextInput
                  label="Product Name"
                  placeholder="Sunglasses"
                  styles={{
                    label: {
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: '24px',
                      color: '#212529',
                      marginBottom: 4,
                    },
                    input: {
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 16,
                      fontWeight: 400,
                      lineHeight: '24px',
                      height: 40,
                      borderColor: '#ced4da',
                      borderRadius: 8,
                      backgroundColor: 'white',
                    },
                  }}
                  {...form.getInputProps('product')}
                />

                <Select
                  label="Country of Origin"
                  placeholder="Select country"
                  data={COUNTRY_OPTIONS}
                  searchable
                  styles={{
                    label: {
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: '24px',
                      color: '#212529',
                      marginBottom: 4,
                    },
                    groupLabel: {
                      textTransform: 'none',
                      letterSpacing: 'normal',
                    },
                    input: {
                      fontFamily: 'Figtree, sans-serif',
                      fontSize: 16,
                      fontWeight: 400,
                      lineHeight: '24px',
                      height: 40,
                      borderColor: '#ced4da',
                      borderRadius: 8,
                      backgroundColor: 'white',
                    },
                  }}
                  {...form.getInputProps('country')}
                />
              </Stack>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={!form.isValid() || isCalculating}
                loading={isCalculating}
                loaderProps={{
                  color: '#ffffff',
                  variant: 'dots',
                }}
                fullWidth
                className={classes.submitButtonVariant}
              >
                {isCalculating ? 'Calculating Tariffs' : 'Calculate Tariffs'}
              </Button>
            </Stack>
          </form>
        </Box>
      </Grid.Col>

      {/* Right column - Hero image or loading state */}
      <Grid.Col span={{ base: 12, md: 6 }} order={{ base: 2, md: 2 }}>
        <Box
          display="flex"
          w="100%"
          h="100%"
          mih={300}
          style={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {!isCalculating && (
            <img
              src={HeroImage}
              alt="Tariff Calculator Hero"
              style={{
                width: '100%',
                height: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          )}
          {isCalculating && (
            <Stack gap="lg" align="flex-start" w="100%" maw={500}>
              <Text
                fz={24}
                fw={600}
                ta="left"
                style={{
                  background: `linear-gradient(to right, ${LIGHT_BROWN}, ${DARK_BROWN})`,
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                {TARIFF_CALC_STRINGS.calculatingHeader}
              </Text>
              <Box w="100%">
                <TariffCalculatorLoadingAnimation />
              </Box>
              <Text
                fz={16}
                fw={600}
                ta="left"
                c={TEXT_ICONS_SECONDARY}
                lh={2.5}
                style={{
                  whiteSpace: 'pre-line',
                }}
              >
                {[
                  TARIFF_CALC_STRINGS.calculatingHTSCodes,
                  TARIFF_CALC_STRINGS.calculatingShouldCost,
                  TARIFF_CALC_STRINGS.calculatingAlternativeSourcing,
                ].join('\n')}
              </Text>
            </Stack>
          )}
        </Box>
      </Grid.Col>
    </Grid>
  )
}
